import { useEffect, useRef, useState } from 'react'
import { Clock, Film, Languages } from 'lucide-react'
import Drawer from '../../components/Drawer'
import SegmentedControl from '../../components/SegmentedControl'
import { EmptyState } from '../../components/States'
import { chapterVideoUrl } from '../../lib/media'
import { listChapters } from './contentService'
import { categoriesStrings as S } from './strings'

const W = S.chapterFiles

/**
 * الملفات المرئية — the legacy's "Chapter files" player.
 *
 * A green video button sits on every row of its topics table and opens a full-screen modal:
 * a playlist of the topic's chapters down one side, a player on the other. The first chapter
 * plays, and when it ends the next one starts. That is the whole feature — it uploads
 * nothing and deletes nothing, it is how an admin checks that the videos a topic holds are
 * the videos they meant to upload.
 *
 * The file is named by two path maps, the topic's and the chapter's:
 *
 *     /root/Content/{Arabic|English}/{category.pathMap}/{chapter.file}.mp4
 *
 * Tenant content is mounted below `wwwroot/root`, matching the learner portal and the
 * migrated on-premises file layout. Keep this path in the shared media helper so the
 * admin preview cannot drift from the learner player again.
 *
 * A chapter has one video per language and they are separate files, so the language is a
 * control here rather than something inherited from the interface: an admin checking an
 * upload needs to see both, whichever language they happen to be reading the panel in.
 */
export default function ChapterFilesDrawer({ open, category, onClose }) {
  const [chapters, setChapters] = useState([])
  const [loading, setLoading] = useState(false)
  const [language, setLanguage] = useState('arabic')
  const [playing, setPlaying] = useState(null)
  /* A chapter whose file is missing — the row stays, so the gap is visible rather than
     silently unplayable. */
  const [missing, setMissing] = useState({})
  const video = useRef(null)

  useEffect(() => {
    if (!open || !category?.id) return undefined

    let cancelled = false
    setLoading(true)
    setMissing({})
    listChapters(category.id)
      .then((rows) => {
        if (cancelled) return
        setChapters(rows)
        setPlaying(rows[0]?.id ?? null)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setChapters([])
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, category])

  const current = chapters.find((chapter) => chapter.id === playing) ?? null
  const source =
    current && category?.pathMap && current.file
      ? chapterVideoUrl(language === 'english' ? 'en' : 'ar', category.pathMap, current.file)
      : null

  /* Auto-advance, as the legacy does on `ended`. */
  const playNext = () => {
    const index = chapters.findIndex((chapter) => chapter.id === playing)
    const next = chapters[index + 1]
    if (next) setPlaying(next.id)
  }

  const title = (chapter) =>
    (language === 'english' ? chapter.titleEn : chapter.titleAr) || chapter.titleAr || chapter.titleEn

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      icon={Film}
      title={W.title}
      subtitle={category?.name}
      onClose={onClose}
    >
      <div className="chfiles">
        <SegmentedControl
          items={[
            { id: 'arabic', label: W.arabic, icon: Languages },
            { id: 'english', label: W.english, icon: Languages },
          ]}
          value={language}
          onChange={setLanguage}
          ariaLabel={W.language}
        />

        {loading ? (
          <p className="chfiles__note">{W.loading}</p>
        ) : chapters.length === 0 ? (
          <EmptyState title={W.empty} />
        ) : (
          <div className="chfiles__body">
            <div className="chfiles__stage">
              {source && !missing[`${language}:${playing}`] ? (
                <video
                  ref={video}
                  className="chfiles__player"
                  src={source}
                  controls
                  autoPlay
                  onEnded={playNext}
                  onError={() => setMissing((was) => ({ ...was, [`${language}:${playing}`]: true }))}
                />
              ) : (
                <p className="chfiles__missing">{W.missing}</p>
              )}
            </div>

            <ol className="chfiles__list">
              {chapters.map((chapter, index) => {
                const gone = missing[`${language}:${chapter.id}`]
                return (
                  <li key={chapter.id}>
                    <button
                      type="button"
                      className={`chfiles__item${chapter.id === playing ? ' is-playing' : ''}`}
                      onClick={() => setPlaying(chapter.id)}
                    >
                      <span className="chfiles__num tabular">{index + 1}</span>
                      <bdi className="chfiles__name">{title(chapter)}</bdi>
                      <span className="chfiles__time tabular">
                        <Clock size={12} strokeWidth={1.75} aria-hidden="true" />
                        {(language === 'english' ? chapter.durationEn : chapter.durationAr) || '00:00'}
                      </span>
                      {gone && <span className="chfiles__gone">{W.missingShort}</span>}
                    </button>
                  </li>
                )
              })}
            </ol>
          </div>
        )}
      </div>
    </Drawer>
  )
}
